import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Notification } from '../../types/notifications';
import { Bell, CheckCircle, Clock, AlertTriangle, MessageSquare, Calendar, FileText } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

export default function NotificationCenter() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'notifications'),
        where('recipientId', '==', user.uid),
        orderBy('createdAt', 'desc')
      ),
      (snapshot) => {
        const notificationData: Notification[] = [];
        snapshot.forEach((doc) => {
          notificationData.push({ id: doc.id, ...doc.data() } as Notification);
        });
        setNotifications(notificationData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading notifications:', error);
        toast.error('Failed to load notifications');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to update notification');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      await Promise.all(
        unreadNotifications.map(n => 
          updateDoc(doc(db, 'notifications', n.id), { read: true })
        )
      );
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to update notifications');
    }
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'task':
        return <CheckCircle className="w-5 h-5 text-primary-500" />;
      case 'message':
        return <MessageSquare className="w-5 h-5 text-secondary-500" />;
      case 'calendar':
        return <Calendar className="w-5 h-5 text-accent-500" />;
      case 'document':
        return <FileText className="w-5 h-5 text-info-DEFAULT" />;
      case 'attendance':
        return <Clock className="w-5 h-5 text-warning-DEFAULT" />;
      case 'system':
        return <Bell className="w-5 h-5 text-text-secondary" />;
      case 'lms':
        return <AlertTriangle className="w-5 h-5 text-error-DEFAULT" />;
      default:
        return <Bell className="w-5 h-5 text-text-secondary" />;
    }
  };

  const filteredNotifications = notifications.filter(n => 
    filter === 'all' || (filter === 'unread' && !n.read)
  );

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-background rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'all' | 'unread')}
            className="px-3 py-1.5 rounded-lg border border-border-light focus:border-primary-500 
                     focus:ring-2 focus:ring-primary-500/20 outline-none transition-all text-sm"
          >
            <option value="all">All Notifications</option>
            <option value="unread">Unread</option>
          </select>
          <span className="text-sm text-text-secondary">
            {notifications.filter(n => !n.read).length} unread
          </span>
        </div>
        
        <button
          onClick={handleMarkAllAsRead}
          className="text-sm text-primary-500 hover:text-primary-600 transition-colors"
        >
          Mark all as read
        </button>
      </div>

      {/* Notifications List */}
      <div className="space-y-2">
        {filteredNotifications.map((notification) => (
          <div
            key={notification.id}
            className={`p-4 rounded-lg transition-colors ${
              notification.read ? 'bg-background' : 'bg-primary-50'
            }`}
          >
            <div className="flex items-start gap-4">
              <div className="mt-1">{getNotificationIcon(notification.type)}</div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-medium text-text-primary">{notification.title}</h3>
                    <p className="text-sm text-text-secondary">{notification.message}</p>
                  </div>
                  <span className="text-xs text-text-muted whitespace-nowrap">
                    {format(new Date(notification.createdAt), 'MMM d, h:mm a')}
                  </span>
                </div>

                <div className="mt-2 flex items-center gap-4">
                  {notification.actionUrl && (
                    <a
                      href={notification.actionUrl}
                      className="text-sm text-primary-500 hover:text-primary-600 transition-colors"
                      onClick={() => !notification.read && handleMarkAsRead(notification.id)}
                    >
                      {notification.metadata?.taskId ? 'View Task' : 
                       notification.metadata?.messageId ? 'View Message' : 
                       'View Details'}
                    </a>
                  )}
                  {!notification.read && (
                    <button
                      onClick={() => handleMarkAsRead(notification.id)}
                      className="text-sm text-text-secondary hover:text-text-primary transition-colors"
                    >
                      Mark as read
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {filteredNotifications.length === 0 && (
          <div className="text-center py-8">
            <Bell className="w-12 h-12 text-text-secondary mx-auto mb-3" />
            <p className="text-text-secondary">No notifications to show</p>
          </div>
        )}
      </div>
    </div>
  );
}